import { typeTestDef } from 'vdg-patients/appointment/diagnosis/test-examination/VdgTestModal'
import AbstractDBModel from './AbstractDBModel'
import FileNote from './FileNote'
import ReferralDetails from './ReferralDetails'

export default interface PatientFile extends AbstractDBModel {
  id: string
  patientId: string
  fileName: string
  category?: string
  description?: string
  doctorsName?: string
  testDate?: string
  testName?: string
  testLocation?: string
  result?: string
  typeTest?: typeTestDef
  notes?: string
  type?: string
  stethomeVisitId?: string
  diagnosisId?: string
  referralDetails: ReferralDetails
  contentType: ContentType
  appointmentId: string
  details: any
  wontDo?: boolean
  isSendToDoctorCheck?: boolean
  isDoctorChecked?: boolean
  status?: FileStatus
  editedFiles?: Array<FileNote>
  examinationStatus?: ExaminationStatus
  /** Why this result needs attention, classified server-side. Drives the severity badge. */
  reason?: patientFileReason
  fileNote?: string
  resultExternalLink: {
    url?: string
    studyId?: string
    jwt?: string
    viewerPath?: string
  }
  patientMedicalResultId?: string
}

export type ContentType = 'image' | 'pdf' | 'video' | 'video/mp4'

export const categories = [
  { key: 'imaging', value: 'imaging', label: 'imaging' },
  { key: 'pathology', value: 'pathology', label: 'pathology' },
  { key: 'laboratory', value: 'laboratory', label: 'laboratory' },
  { key: 'stethome', value: 'stethome', label: 'stethome' },
  { key: 'perscription', value: 'perscription', label: 'perscription' },
  { key: 'examination', value: 'examination', label: 'examination' },
  { key: 'referral', value: 'referral', label: 'referral' },
  { key: 'result', value: 'result', label: 'result' },
  { key: 'uncategorized', value: undefined, label: 'uncategorized' },
]

export function getTypeForServer(type: string) {
  if (type === 'Lungs') {
    return 'pulmonary'
  }
  if (type === 'Heart') {
    return 'cardiac'
  }
  return type
}

export enum FileStatus {
  doctorChecked = 'doctorChecked',
  waitingForDoctorCheck = 'waitingForDoctorCheck',
  needToSendForCheck = 'needToSendForCheck',
}

export enum ExaminationStatus {
  viewdTestResult = 'viewdTestResult',
  notViewdTestResult = 'notViewdTestResult',
  waitingForInterpretation = 'waitingForInterpretation',
  interpreted = 'interpreted',
}

export enum patientFileReason {
  lastExamination = 'lastExamination',
  outOfRangeResident = 'outOfRangeResident',
  testBasic = 'testBasic',
  outOfRangeAdmitted = 'outOfRangeAdmitted',
  nurseAssesmentUrgent = 'nurseAssesmentUrgent',
  nurseAssesmentBasic = 'nurseAssesmentBasic',
  outOfRangeSignificant = 'outOfRangeSignificant',
  testASAP = 'testASAP',
  nurseAssesmentUrgentOutOfRangeResident = 'nurseAssesmentUrgent#outOfRangeResident',
  nurseAssesmentUrgentOutOfRangeAdmitted = 'nurseAssesmentUrgent#outOfRangeAdmitted',
  nurseAssesmentUrgentOutOfRangeSignificant = 'nurseAssesmentUrgent#outOfRangeSignificant',
  nurseAssesmentBasicOutOfRangeResident = 'nurseAssesmentBasic#outOfRangeResident',
  nurseAssesmentBasicOutOfRangeAdmitted = 'nurseAssesmentBasic#outOfRangeAdmitted',
  nurseAssesmentBasicOutOfRangeSignificant = 'nurseAssesmentBasic#outOfRangeSignificant',
}

/** How prominently a reason is shown. Severity, never review state — the two are independent. */
export enum ReasonSeverity {
  critical = 'critical',
  abnormal = 'abnormal',
}

// Only out-of-range reasons say anything about the result itself. Reasons like testASAP or
// urgent describe how quickly the test was *ordered*, not whether what came back is abnormal, so
// they carry no severity here. This classifies a decision the server has already made — it does
// not evaluate results or thresholds, so the clinical logic that decides whether a result is
// abnormal stays untouched.
//
// Matched per '#'-separated part, because a reason can combine a nurse assessment with an
// out-of-range finding (e.g. 'nurseAssesmentUrgent#outOfRangeSignificant').
const CRITICAL_REASON_PARTS = ['outOfRangeSignificant']

const ABNORMAL_REASON_PARTS = ['outOfRangeResident', 'outOfRangeAdmitted']

/**
 * Severity of a result's reason, or undefined when the reason carries none (a normal result, a
 * routine examination). A result can be abnormal and reviewed, or normal and unreviewed — this
 * says nothing about whether anyone has looked at it.
 */
export const getReasonSeverity = (reason?: string): ReasonSeverity | undefined => {
  if (!reason) return undefined

  const parts = reason.split('#')
  if (parts.some(part => CRITICAL_REASON_PARTS.includes(part))) return ReasonSeverity.critical
  if (parts.some(part => ABNORMAL_REASON_PARTS.includes(part))) return ReasonSeverity.abnormal
  return undefined
}

export enum ExaminationDetailsName {
  glucose = 'glucose',
  weight = 'weight',
  heart = 'heart',
  temperature = 'temperature',
  bloodPressure = 'bloodPressure',
  spo2 = 'Spo2',
  steps = 'steps',
}
